import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useServer } from "@/lib/server-context";
import { useChannels } from "@/lib/feature-hooks";
import { toast } from "sonner";
import { Plus, Bell, Trash2 } from "lucide-react";
import EmptyServer from "@/components/dashboard/EmptyServer";

export default function RemindersPanel() {
  const { active } = useServer();
  const channels = useChannels(active?.id, active?.bot_present);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ channel_id: "", message: "", remind_at: "" });

  const refresh = () => {
    if (!active?.id || !active.bot_present) return;
    api.get(`/features/reminders/${active.id}`).then((r) => setList(r.data));
  };
  useEffect(refresh, [active?.id, active?.bot_present]);

  if (!active) return <EmptyServer />;
  if (!active.bot_present) return <EmptyServer needsBot />;

  const create = async (e) => {
    e.preventDefault();
    if (!form.channel_id || !form.message || !form.remind_at) return;
    await api.post(`/features/reminders/${active.id}`, { ...form, remind_at: new Date(form.remind_at).toISOString() });
    toast.success("Reminder scheduled");
    setForm({ channel_id: "", message: "", remind_at: "" });
    refresh();
  };

  const remove = async (id) => {
    await api.delete(`/features/reminders/${active.id}/${id}`);
    refresh();
  };

  return (
    <div data-testid="reminders-panel" className="space-y-8">
      <div>
        <div className="overline mb-2"><Bell className="inline w-4 h-4 mr-2 -mt-1" />UTILITIES</div>
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight">Reminders</h1>
        <p className="text-zinc-400 mt-2">Schedule one-time messages to be posted at a future time.</p>
      </div>

      <form onSubmit={create} className="tactical-card p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="rem-form">
        <select className="tactical-input" value={form.channel_id} onChange={(e) => setForm({ ...form, channel_id: e.target.value })} data-testid="rem-channel">
          <option value="">— Channel —</option>
          {channels.filter((c) => c.type === "text").map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
        </select>
        <input className="tactical-input" placeholder="Message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="rem-message" />
        <input type="datetime-local" className="tactical-input" value={form.remind_at} onChange={(e) => setForm({ ...form, remind_at: e.target.value })} data-testid="rem-time" />
        <div className="md:col-span-3 flex justify-end">
          <button type="submit" className="btn-primary" data-testid="rem-create-btn"><Plus className="w-4 h-4" /> Schedule</button>
        </div>
      </form>

      <div className="tactical-card p-6">
        <h2 className="font-heading text-xl uppercase mb-4">Scheduled ({list.length})</h2>
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="border border-brand-line p-3 rounded-lg flex items-center gap-3" data-testid={`rem-row-${r.id}`}>
              <Bell className="w-4 h-4 text-brand-orange" />
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{r.message}</div>
                <div className="text-xs text-zinc-500">@ {new Date(r.remind_at).toLocaleString()} · by {r.created_by}</div>
              </div>
              <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
